import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { ProductApiService, ProductOption } from 'src/app/core/services/product-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { filterRecordsBySearch, paginateClientRecords } from 'src/app/shared/utils/client-list.util';

type ProductListRow = ProductOption & Record<string, unknown>;

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
  ],
  templateUrl: './products.component.html',
})
export class ProductsComponent implements OnInit {
  private readonly api = inject(ProductApiService);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search name, slug…' },
      status: false,
      dateRange: false,
    },
    pagination: true,
  };

  readonly columns: ListModuleColumnDef[] = [
    { id: 'name', header: 'Name' },
    { id: 'slug', header: 'Slug' },
    { id: 'actions', header: 'Actions', align: 'right' },
  ];

  readonly products = signal<ProductOption[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  currentPage = 1;
  readonly perPage = 10;

  ngOnInit(): void {
    this.load();
  }

  get filteredRecords(): ProductListRow[] {
    return filterRecordsBySearch(this.searchRows, this.listFilters.searchQuery, ['name', 'slug']);
  }

  get pagedRecords(): ProductListRow[] {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).pageRows;
  }

  get paginationMeta(): ListModulePaginationMeta {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).meta;
  }

  get emptyMessage(): string {
    if (this.products().length) return 'No products match your search.';
    return 'No products yet.';
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.currentPage = 1;
  }

  onListPageChange(page: number): void {
    this.currentPage = page;
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .listForDropdown()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          this.products.set(res.data ?? []);
          this.currentPage = 1;
        },
        error: (err) => this.error.set(resolveApiErrorMessage(err)),
      });
  }

  private get searchRows(): ProductListRow[] {
    return this.products() as ProductListRow[];
  }
}
